"use client"

import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, startOfDay } from "date-fns"
import { cn } from "@/lib/utils"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import type { CalendarEvent } from "./calendar"
import UserSvg from "@/assets/userSvg.svg"
import { CalendarEventActions } from "./calendar-event-actions"

interface MonthViewProps {
    currentDate: Date
    events: CalendarEvent[]
    dateRange: { start: Date; end: Date }
    onViewEvent?: (event: CalendarEvent) => void
    onEditEvent?: (event: CalendarEvent) => void
    onDeleteEvent?: (event: CalendarEvent) => void
    onDateClick?: (date: Date) => void
    refetch?: () => void
    access?: string
    hideEventActions?: boolean
}

export function MonthView({ currentDate, events, dateRange, onViewEvent, onEditEvent, onDeleteEvent, onDateClick, refetch, access, hideEventActions }: MonthViewProps) {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(monthStart)
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 })
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 })

    const rows = []
    let days = []
    let day = startDate
    const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]

    // Get events for a specific day
    const getEventsForDay = (day: Date) => {
        return events.filter((event) => isSameDay(new Date(event.start), day))
    }

    // Create calendar rows
    while (day <= endDate) {
        for (let i = 0; i < 7; i++) {
            const currentDay = day
            const dayEvents = getEventsForDay(currentDay)
            const isCurrentMonth = isSameMonth(day, monthStart)
            const isPast = currentDay < startOfDay(new Date())
            const isToday = isSameDay(currentDay, new Date())
            const hasEvents = dayEvents.length > 0
            const isClickable = onDateClick && (!isPast || isToday)

            days.push(
                <div
                    key={day.toString()}
                    className={cn(
                        "border-r border-b border-gray-200 min-h-[100px] p-1 group",
                        !isCurrentMonth && "bg-gray-50/30",
                        isPast && !isToday && "bg-gray-100/50 grayscale-[0.5]",
                        i === 3 && "bg-green-50/30", // Highlight Wednesday like in the screenshot
                        "overflow-y-auto",
                        isClickable ? "cursor-pointer" : "cursor-default",
                    )}
                    onClick={(e) => {
                        // Only trigger date-click when clicking the empty cell area,
                        // not on event pills or their action buttons
                        if (
                            isClickable &&
                            e.target instanceof Element &&
                            !e.target.closest('[data-calendar-event]')
                        ) {
                            onDateClick(new Date(currentDay))
                        }
                    }}
                >
                    <div className="flex flex-col h-full">
                        <div className={cn(
                            "text-sm mb-1 w-7 h-7 flex items-center justify-center rounded-full transition-colors font-medium",
                            isSameDay(day, new Date()) ? "bg-primary text-white" : cn(
                                !isCurrentMonth && "text-gray-400",
                                onDateClick && "group-hover:bg-primary/10 group-hover:text-primary"
                            )
                        )}>
                            {format(day, "d")}
                            {day.getDate() === 1 && <span className="text-xs text-gray-500 ml-1">{format(day, "MMM")}</span>}
                        </div>
                        <div className="flex-1 space-y-1">
                            {dayEvents.map((event) => {
                                // Map color to static Tailwind classes
                                const getColorClasses = (color?: string) => {
                                    const colorMap: Record<string, string> = {
                                        purple: "bg-purple-200 border-purple-400 text-purple-900 hover:bg-purple-250",
                                        blue: "bg-blue-200 border-blue-400 text-blue-900 hover:bg-blue-250",
                                        green: "bg-green-200 border-green-400 text-green-900 hover:bg-green-250",
                                        red: "bg-red-200 border-red-400 text-red-900 hover:bg-red-250",
                                        yellow: "bg-yellow-200 border-yellow-400 text-yellow-900 hover:bg-yellow-250",
                                        orange: "bg-orange-200 border-orange-400 text-orange-900 hover:bg-orange-250",
                                        teal: "bg-teal-200 border-teal-400 text-teal-900 hover:bg-teal-250",
                                        pink: "bg-pink-200 border-pink-400 text-pink-900 hover:bg-pink-250",
                                        indigo: "bg-indigo-200 border-indigo-400 text-indigo-900 hover:bg-indigo-250",
                                        gray: "bg-gray-200 border-gray-400 text-gray-900 hover:bg-gray-250",
                                    };
                                    return colorMap[color || ""] || "bg-blue-200 border-blue-400 text-blue-900 hover:bg-blue-250";
                                };

                                return (
                                    <div
                                        key={event.id}
                                        data-calendar-event="true"
                                        className={cn(
                                            "text-xs p-1 rounded-md border shadow-sm transition-colors",
                                            getColorClasses(event.color),
                                            onViewEvent && "cursor-pointer"
                                        )}
                                        onClick={() => onViewEvent?.(event)}
                                    >
                                        <div className="flex items-start gap-1 mb-1">
                                            {event.user?.avatar && (
                                                <Avatar className="h-4 w-4">
                                                    <AvatarImage
                                                        src={`${import.meta.env.VITE_API_URL}/uploads/${event.user.avatar}`}
                                                        alt={event.user.name || "User"}
                                                    />
                                                    <AvatarFallback>
                                                        <img src={UserSvg} alt="User" className="h-4 w-4 rounded-full" />
                                                    </AvatarFallback>
                                                </Avatar>
                                            )}
                                            <div
                                                className="font-medium flex-1 cursor-pointer break-words leading-tight text-[11px] line-clamp-2"
                                                onClick={() => onViewEvent?.(event)}
                                                title={event.title}
                                            >
                                                {event.title}
                                            </div>
                                            {!hideEventActions && (
                                                <CalendarEventActions
                                                    event={event}
                                                    onEdit={onEditEvent}
                                                    onView={onViewEvent}
                                                    onDelete={onDeleteEvent}
                                                    refetch={refetch}
                                                    access={access}
                                                    size="sm"
                                                    variant="dropdown"
                                                />
                                            )}
                                        </div>
                                        {event.tags && (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {event.tags.map((tag, idx) => (
                                                    <span
                                                        key={idx}
                                                        className={cn("px-1.5 py-0.5 rounded-sm text-white text-[10px]", `bg-${tag.color}-500`)}
                                                    >
                                                        {tag.label}
                                                    </span>
                                                ))}
                                                {event.is_exclusive && (
                                                    <span
                                                        className={cn("px-1.5 py-0.5 rounded-sm text-white text-[10px]", `bg-red-500 text-white`)}
                                                    >
                                                        Exclusive
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>,
            )
            day = addDays(day, 1)
        }
        rows.push(
            <div key={day.toString()} className="grid grid-cols-7">
                {days}
            </div>,
        )
        days = []
    }


    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            <div className="sticky top-0 z-10 grid grid-cols-7 border-b bg-white shrink-0">
                {daysOfWeek.map((day, index) => (
                    <div
                        key={day}
                        className={cn(
                            "py-2 text-center text-[13px] border-r",
                            index === 3 && "bg-green-50/30", // Highlight Wednesday header
                        )}
                    >
                        {day}
                    </div>
                ))}
            </div>
            <div className="flex-1 overflow-y-auto">{rows}</div>
        </div>
    )
}

